import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DrawingCanvas from './DrawingCanvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo du chatbot (robot avec lunettes)
const ChatbotLogo = () => (
  <div className="flex-shrink-0">
    <img 
      src="https://static.vecteezy.com/system/resources/previews/055/582/618/non_2x/minimalist-blue-robot-face-logo-vector.jpg" 
      alt="Assistant Opticien IA" 
      className="w-10 h-10 rounded-full border-2 border-blue-500 object-cover"
    />
  </div>
);

const ChatExpert = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(true); // Optimiste par défaut
  const [showDrawing, setShowDrawing] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll vers le bas quand de nouveaux messages arrivent
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Charger les sessions existantes au montage
  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/chat/sessions`);
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Erreur lors du chargement des sessions:', error);
      if (error.response?.status === 403) {
        setHasSubscription(false);
      }
    }
  };

  const loadSessionHistory = async (sid) => {
    try {
      const response = await axios.get(`${API}/chat/history`, {
        params: { session_id: sid }
      });
      
      if (response.data.messages) {
        setMessages(response.data.messages);
        setSessionId(sid);
        setActiveSessionId(sid);
        setShowSessions(false);
      }
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
    }
  };

  const sendMessage = async (imageData = null, customMessage = null) => {
    const messageToSend = customMessage || inputMessage;
    
    if ((!messageToSend.trim() && !imageData) || isLoading) return;

    const messageContent = imageData 
      ? messageToSend
      : messageToSend;

    const userMessage = {
      role: 'user',
      content: messageContent,
      timestamp: new Date().toISOString(),
      image: imageData
    };

    // Ajouter le message utilisateur immédiatement
    setMessages(prev => [...prev, userMessage]);
    if (!customMessage) {
      setInputMessage('');
    }
    setIsLoading(true);

    try {
      const requestData = {
        message: messageToSend,
        session_id: sessionId
      };

      // Si on a une image, l'ajouter au payload
      if (imageData) {
        requestData.image = imageData;
      }

      const response = await axios.post(`${API}/chat/expert`, requestData);

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
      
      // Mettre à jour la session ID si c'est une nouvelle conversation
      if (!sessionId) {
        setSessionId(response.data.session_id);
        setActiveSessionId(response.data.session_id);
        loadSessions(); // Recharger la liste des sessions
      }

    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      
      if (error.response?.status === 403) {
        setHasSubscription(false);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '⚠️ ' + (error.response?.data?.detail || 'Accès refusé. Un abonnement actif est requis pour utiliser l\'Assistant Expert IA.'),
          timestamp: new Date().toISOString()
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: '❌ Erreur lors de la communication avec l\'assistant. Veuillez réessayer.',
          timestamp: new Date().toISOString()
        }]);
      }
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleImageCapture = (imageData, description) => {
    setShowDrawing(false);
    // Envoyer le message avec l'image et la description
    const messageContent = description || 'Peux-tu identifier cette gravure ?';
    setInputMessage('');
    sendMessage(imageData, messageContent);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const startNewConversation = () => {
    setMessages([]);
    setSessionId(null);
    setActiveSessionId(null);
    setShowSessions(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (sid) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette conversation ?')) return;

    try {
      await axios.delete(`${API}/chat/session/${sid}`);
      
      // Si la session supprimée est active, démarrer une nouvelle conversation
      if (sid === activeSessionId) {
        startNewConversation();
      }
      
      // Recharger les sessions
      loadSessions();
    } catch (error) {
      console.error('Erreur lors de la suppression de la session:', error);
      alert('Erreur lors de la suppression de la conversation');
    }
  };

  const exportToPDF = async () => {
    if (!sessionId) {
      alert('Aucune conversation à exporter');
      return;
    }

    try {
      const response = await axios.get(`${API}/chat/export-pdf/${sessionId}`, {
        responseType: 'blob'
      });

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `opticlink_conversation_${sessionId.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF');
    }
  };

  // Message de bienvenue si pas de messages
  const welcomeMessage = {
    role: 'assistant',
    content: `Bonjour ! 👋 Je suis votre Assistant Expert IA spécialisé en optique.

Je peux vous aider avec :
• 👓 Conseils sur les verres ophtalmiques (progressifs, unifocaux, etc.)
• 👁️ Contactologie (lentilles souples, rigides, toriques)
• 🎨 Choix de montures et adaptation
• 📏 Prise de mesures et centrage
• 🔬 Traitements optiques et matériaux
• 📋 Interprétation d'ordonnances
• ⚙️ Résolution de problèmes techniques
• 📚 Normes et standards professionnels

Comment puis-je vous aider aujourd'hui ?`,
    timestamp: new Date().toISOString()
  };

  const displayMessages = messages.length > 0 ? messages : [welcomeMessage];

  if (!hasSubscription) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="max-w-2xl w-full bg-slate-800/50 rounded-2xl p-8 text-center">
          <ChatbotLogo />
          <h2 className="text-2xl font-bold text-white mt-4 mb-2">
            Abonnement Requis
          </h2>
          <p className="text-slate-300 mb-6">
            L'accès à l'Assistant Expert IA en Optique est réservé aux utilisateurs avec un abonnement actif.
          </p>
          <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg p-4">
            <p className="text-blue-300">
              💎 Souscrivez à un abonnement mensuel pour débloquer cette fonctionnalité premium
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-250px)] gap-4">
      {/* Sidebar - Liste des sessions */}
      {showSessions && (
        <div className="w-80 bg-slate-800/50 rounded-2xl border border-slate-600/50 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-slate-600/50">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Conversations</h3>
              <button
                onClick={() => setShowSessions(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {sessions.map((session) => (
              <div
                key={session.session_id}
                className={`p-3 rounded-lg cursor-pointer transition-all ${
                  session.session_id === activeSessionId
                    ? 'bg-blue-600/30 border border-blue-500/30'
                    : 'bg-slate-700/50 hover:bg-slate-700'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div
                    className="flex-1 min-w-0"
                    onClick={() => loadSessionHistory(session.session_id)}
                  >
                    <p className="text-sm text-white truncate">{session.preview}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-slate-400">
                        {session.message_count} messages
                      </span>
                      <span className="text-xs text-slate-500">•</span>
                      <span className="text-xs text-slate-400">
                        {new Date(session.last_activity).toLocaleDateString('fr-FR')}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteSession(session.session_id);
                    }}
                    className="ml-2 text-red-400 hover:text-red-300 transition-colors"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
            
            {sessions.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                Aucune conversation sauvegardée
              </div>
            )}
          </div>
        </div>
      )}

      {/* Zone de chat principale */}
      <div className="flex-1 flex flex-col bg-slate-800/50 rounded-2xl border border-slate-600/50 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-600/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ChatbotLogo />
            <div>
              <h2 className="text-lg font-semibold text-white">Assistant Opticien IA</h2>
              <p className="text-xs text-slate-400">Expert en optique et lunetterie</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSessions(!showSessions)}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm"
              title="Voir les conversations"
            >
              📋 Historique
            </button>
            <button
              onClick={startNewConversation}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
              title="Nouvelle conversation"
            >
              ➕ Nouveau
            </button>
            {sessionId && (
              <button
                onClick={exportToPDF}
                className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
                title="Exporter en PDF"
              >
                📄 PDF
              </button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {displayMessages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} items-start gap-3`}
            >
              {message.role === 'assistant' && <ChatbotLogo />}
              
              <div
                className={`max-w-[70%] p-4 rounded-2xl ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-100'
                }`}
              >
                {message.image && (
                  <div className="mb-3 rounded-lg overflow-hidden border-2 border-white/20">
                    <img 
                      src={message.image} 
                      alt="Dessin de gravure" 
                      className="w-full max-w-md"
                    />
                  </div>
                )}
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.content}</p>
                {message.timestamp && (
                  <p className="text-xs opacity-60 mt-2">
                    {new Date(message.timestamp).toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
              
              {message.role === 'user' && (
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold">
                    👤
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start items-start gap-3">
              <ChatbotLogo />
              <div className="bg-slate-700 p-4 rounded-2xl">
                <div className="flex space-x-2">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-600/50">
          <div className="flex gap-2 mb-2">
            <button
              onClick={() => setShowDrawing(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2"
              title="Dessiner une gravure"
            >
              ✏️ Dessiner une gravure
            </button>
          </div>
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Posez votre question sur l'optique ou décrivez une gravure..."
              className="flex-1 bg-slate-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              rows="2"
              disabled={isLoading}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!inputMessage.trim() || isLoading}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-xl transition-colors font-medium"
            >
              {isLoading ? '...' : '➤'}
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">
            L'Assistant IA peut faire des erreurs. Vérifiez toujours les informations importantes.
          </p>
        </div>
      </div>

      {/* Drawing Canvas Modal */}
      {showDrawing && (
        <DrawingCanvas
          onImageCapture={handleImageCapture}
          onClose={() => setShowDrawing(false)}
        />
      )}
    </div>
  );
};

export default ChatExpert;
